import type { SessionStatus } from './types'

export type SessionStatusInput = {
  /** Вход завершён — успешно или нет. */
  isResolved: boolean
  /** Войти не удалось. */
  hasAuthError: boolean
  /** Платформа отдала пользователя. */
  hasUser: boolean
  /**
   * Что говорит сервер: `true` — `onboardedAt` заполнен, `false` — пуст,
   * `null` — профиль ещё не приехал.
   */
  serverOnboarded: boolean | null
  /** Локальный признак «регистрацию проходили» (LocalStorage). */
  onboardedCache: boolean
  /** Онбординг завершён прямо сейчас, в этом запуске приложения. */
  hasJustCompleted: boolean
  /** Профиль не загрузился. */
  isMeError: boolean
}

/**
 * Чистая часть `SessionProvider`: из чего складывается статус сессии.
 *
 * Порядок правил здесь важнее их содержания, поэтому логика вынесена из провайдера и покрыта
 * тестами — именно её перекос давал лишний кадр онбординга после его прохождения.
 */
export function resolveSessionStatus(input: SessionStatusInput): SessionStatus {
  const { isResolved, hasAuthError, hasUser, serverOnboarded, onboardedCache, hasJustCompleted } =
    input

  if (!isResolved) {
    return 'loading'
  }

  if (hasAuthError || !hasUser) {
    return 'anonymous'
  }

  // Завершённый онбординг в рамках запуска назад не отыгрывается — и это главное правило.
  // Ответ `GET /users/me`, уехавший ДО отметки, приходит с `onboarded_at: null` и без этой
  // проверки возвращал бы человека в регистрацию уже после того, как он из неё вышел.
  if (hasJustCompleted) {
    return 'ready'
  }

  if (serverOnboarded !== null) {
    return serverOnboarded ? 'ready' : 'onboarding'
  }

  // Сервер ещё не ответил. Кэш говорит «проходила» — показываем приложение сразу.
  if (onboardedCache) {
    return 'ready'
  }

  // Кэша нет. Сеть отвалилась — пускаем в онбординг (иначе вечный сплэш), иначе ждём ответ:
  // онбординг зарегистрированному человеку показывать нельзя.
  return input.isMeError ? 'onboarding' : 'loading'
}
